// The energy a side just paid, so the views can show it leave the pips. A
// cast of a skill pays into the slot of that skill. In Godsmode the energy
// is on the pieces, so this reads `energyOf`. A piece that leaves the board
// with energy also shows as a spend, for no skill. The flow takes the energy
// at once. This is presentation, and the
// game state never waits for it. Garrett, 2026-10-12: "sound/effects when
// they are spent? same for enemy when enemy spends".

import type { Side } from '../engine/types';
import { energyOf, type GameState } from '../game/state';
import { createStore } from '../lib/store';
import type { SpellId } from '../spells/types';
import { gameStore } from './game';

export type Spend = {
  /** Counts up, so two spends of the same size are two spends. */
  serial: number;
  side: Side;
  /** The energy of the side before it paid. */
  before: number;
  /** The energy it paid. */
  count: number;
  /** The skill the energy paid for. Null when it paid for no spell. */
  spellId: SpellId | null;
};

const SIDES: readonly Side[] = ['gold', 'violet'];

export const spendStore = createStore<Spend | null>(null);

/** The skill a side cast between two states, or null. */
const castBetween = (before: GameState, after: GameState, side: Side): SpellId | null => {
  const cast = after.lastCast;
  return cast !== null && cast !== before.lastCast && cast.caster === side ? cast.spellId : null;
};

/** Call once at startup, after a saved match loads, so a load shows no spend. */
export const startEnergySpend = (): void => {
  let last = gameStore.get();
  let serial = 0;
  gameStore.subscribe((state) => {
    const before = last;
    last = state;
    // The first line of the log is new in each match. A new match pays nothing.
    if (state.log[0] !== before.log[0]) return;
    for (const side of SIDES) {
      const held = energyOf(before, side);
      const count = held - energyOf(state, side);
      if (count <= 0) continue;
      serial += 1;
      spendStore.set({
        serial,
        side,
        before: held,
        count,
        spellId: castBetween(before, state, side),
      });
    }
  });
};
